<template>
  <div id="page-change-logs-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <vue-element-loading :active="busy"></vue-element-loading>
        
        <!-- Side-by-side Original and Changed Values -->
        <vs-row>
          <vs-col vs-w="6" class="pr-2">
            <card-page card-title="Original Values" class="mb-6">
              <vue-form-generator
                ref="originalForm"
                :model="originalModel"
                :options="formOptions"
                :schema="highlightedOriginalSchema"
              />
            </card-page>
          </vs-col>
          <vs-col vs-w="6" class="pl-2">
            <card-page card-title="New Values" class="mb-6">
              <vue-form-generator
                ref="changedForm"
                :model="changedModel"
                :options="formOptions"
                :schema="highlightedSchema"
              />
            </card-page>
          </vs-col>
        </vs-row>

        <!-- Additive Widget Sections -->
        <vs-row v-for="section in additiveSections" :key="section.key">
          <vs-col vs-w="6" class="pr-2">
            <card-page :card-title="section.title" class="mb-6">
              <relationship-list
                :items="section.originalData"
                :empty-message="`No ${section.title.toLowerCase()}`"
                :item-label="section.itemLabel"
                :original-items="section.originalData"
                :is-changed-section="false"
              />
            </card-page>
          </vs-col>
          <vs-col vs-w="6" class="pl-2">
            <card-page :card-title="section.title" class="mb-6">
              <relationship-list
                :items="section.changedData"
                :empty-message="`No ${section.title.toLowerCase()}`"
                :item-label="section.itemLabel"
                :original-items="section.originalData"
                :is-changed-section="true"
              />
            </card-page>
          </vs-col>
        </vs-row>

        <!-- Action Buttons -->
        <div v-if="componentAccess.canPublish.access" class="flex justify-between mt-6">
          <vs-button @click="$emit('back')" color="light">
            Back
          </vs-button>
          <div v-if="isLatest" class="flex">
            <vs-button
              color="success"
              :disabled="processing"
              @click="handleApprove"
              class="mr-4"
            >
              Approve
            </vs-button>
            <vs-button
              color="danger"
              :disabled="processing"
              @click="handleReject"
            >
              Reject
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import {datepickerOptions} from '@/components/form/FormElementOption';
import CardPage from '../../../components/Views/CardPage';
import RelationshipList from './RelationshipList';
import CanAccessMixin from '@/components/mixins/CanAccessMixin';

// Field configuration for form generation
const FIELD_CONFIG = {
  disabled: true,
  styleClasses: 'vx-col w-1/3 p-4'
};

// Additive widget configuration
const ADDITIVE_WIDGETS = [
  {
    key: 'placing-brokers',
    title: 'Placing Brokers',
    itemLabel: 'Placing Broker',
    originalProp: 'originalPlacingBrokers',
    changedProp: 'changedPlacingBrokers',
    modelKey: 'placing_brokers'
  },
  {
    key: 'cover-holders',
    title: 'Cover Holders',
    itemLabel: 'Cover Holder',
    originalProp: 'originalCoverHolders',
    changedProp: 'changedCoverHolders',
    modelKey: 'coverholders'
  },
  {
    key: 'facility-managers',
    title: 'Facility Managers',
    itemLabel: 'Facility Manager',
    originalProp: 'originalManagers',
    changedProp: 'changedManagers',
    modelKey: 'managers'
  },
  {
    key: 'intermediary',
    title: 'Intermediary',
    itemLabel: 'Intermediary',
    originalProp: 'originalIntermediary',
    changedProp: 'changedIntermediary',
    modelKey: 'intermediary'
  }
];

// Fallback display name mappings
const FALLBACK_MAPPINGS = [
  { fk: 'ClassOfBusiness_FK', name: 'ClassOfBusiness_Name' },
  { fk: 'FacilityType_FK', name: 'FacilityType_Name' },
  { fk: 'CompanyLossAdjuster_FK', name: 'CompanyLossAdjuster_Name' },
  { fk: 'TerritoryRiskLocation_FK', name: 'TerritoryRiskLocation_Name' },
  { fk: 'TerritoryInsuredDomicile_FK', name: 'TerritoryInsuredDomicile_Name' },
  { fk: 'TerritoryTerritorialLimit_FK', name: 'TerritoryTerritorialLimit_Name' },
  { fk: 'CompanyLeadInsurer_FK', name: 'CompanyLeadInsurer_Name' }
];

export default {
  name: 'ChangeLogsForm',
  components: {
    CardPage,
    RelationshipList
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    isLatest: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      originalModel: {},
      changedModel: {},
      busy: false,
      processing: false,
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: false
      },
      // Additive widget data - dynamically populated
      ...ADDITIVE_WIDGETS.reduce((acc, widget) => {
        acc[widget.originalProp] = [];
        acc[widget.changedProp] = [];
        return acc;
      }, {}),
      baseSchema: this.createBaseSchema(),
      componentAccess: {
        canPublish: {
          route: 'facilities.change-logs.publish',
          access: false,
        },
      }
    };
  },
  mixins: [CanAccessMixin],
  computed: {
    // Generate additive sections for template iteration
    additiveSections() {
      return ADDITIVE_WIDGETS.map(widget => ({
        key: widget.key,
        title: widget.title,
        itemLabel: widget.itemLabel,
        originalData: this[widget.originalProp],
        changedData: this[widget.changedProp]
      }));
    },
    
    highlightedSchema() {
      return this.createHighlightedSchema('changedForm');
    },
    
    highlightedOriginalSchema() {
      return this.createHighlightedSchema('originalForm');
    }
  },
  watch: {
    value: {
      handler(newValue) {
        if (newValue) {
          this.parseChangeLogData(newValue);
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // Optimized schema cloning
    cloneSchema(schema) {
      return {
        groups: schema.groups.map(group => ({
          ...group,
          fields: group.fields ? group.fields.map(field => ({ ...field })) : undefined
        }))
      };
    },

    // Create base form schema
    createBaseSchema() {
      return {
        groups: [
          {
            styleClasses: 'w-full mt-0',
            legend: 'General',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Reference', model: 'FacilityReference' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Unique Market Reference', model: 'UniqueMarketReference' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Year of Account', model: 'YearOfAccount' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Class of Business', model: 'ClassOfBusiness_Name' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Facility Type', model: 'FacilityType_Name' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Capacity Limit', model: 'CapacityLimit' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Risk Location', model: 'TerritoryRiskLocation_Name' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Insured Domicile', model: 'TerritoryInsuredDomicile_Name' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Territorial Limit', model: 'TerritoryTerritorialLimit_Name' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Company Lead Insurer', model: 'CompanyLeadInsurer_Name' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'text', label: 'Company Loss Adjuster', model: 'CompanyLossAdjuster_Name' },
              { ...FIELD_CONFIG, type: 'vsCheckbox', label: 'Use company lead logo', model: 'UseCompanyLeadLogo' }
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Dates',
            fields: [
              { ...FIELD_CONFIG, ...datepickerOptions, label: 'Inception Date', model: 'InceptionDate' },
              { ...FIELD_CONFIG, ...datepickerOptions, label: 'Expiry Date', model: 'ExpiryDate' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, ...datepickerOptions, label: 'BA Ref Date', model: 'BARefDate' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'BA Ref #', model: 'BARefNo' }
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Figures',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Signing %', model: 'Signing_PC' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Notifiable %', model: 'Notifiable_PC' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Maximum Facility Discount %', model: 'MaximumFacilityDiscount_PC' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Contingent Commission %', model: 'ContingentCommission_PC' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Profit Commission', model: 'ProfitCommission_PC' },
              { ...FIELD_CONFIG, ...datepickerOptions, label: 'Profit Commission Date', model: 'ProfitCommissionDate' }
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Limits',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Period of Insurance (Months)', model: 'PeriodOfInsuranceMonth' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Maximum Period (Months)', model: 'MaximumPeriodMonth' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Maximum Back Date (Days)', model: 'MaximumBackDateDay' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Maximum Advance Date (Days)', model: 'MaximumAdvanceDay' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Notice Period (Days)', model: 'NoticePeriodDay' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Remittance (Days)', model: 'RemittanceDay' }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Production Reporting Period (Days)', model: 'ProductionReportingPeriodDay' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Paid Premium Reporting (Days)', model: 'PaidPremiumReportingPeriodDay' },
              { ...FIELD_CONFIG, type: 'input', inputType: 'number', label: 'Claims Reporting Period (Days)', model: 'ClaimsReportingPeriodDay' }
            ]
          }
        ]
      };
    },

    // Create highlighted schema with shared logic
    createHighlightedSchema(formRef) {
      const schema = this.cloneSchema(this.baseSchema);
      const changedFields = Object.keys(this.changedModel);
      
      this.addHighlightingToSchema(schema, changedFields, formRef);
      return schema;
    },

    // Add highlighting to schema fields
    addHighlightingToSchema(schema, changedFields, formRef) {
      const addHighlighting = (fields) => {
        fields.forEach(field => {
          if (changedFields.includes(field.model)) {
            this.applyFieldHighlighting(field, formRef);
          }
        });
      };
      
      schema.groups.forEach(group => {
        if (group.fields) {
          addHighlighting(group.fields);
        }
      });
    },

    // Apply highlighting to a field
    applyFieldHighlighting(field, formRef) {
      field.styleClasses = (field.styleClasses || '') + ' changed-field-highlight';
      field.attributes = { ...(field.attributes || {}), 'data-changed': 'true' };
    },

    // Parse change log data
    parseChangeLogData(changeLog) {
      try {
        this.originalModel = this.addFallbackDisplayNames(changeLog.Original || {});
        this.changedModel = this.addFallbackDisplayNames(changeLog.Changes || {});
        this.parseAdditiveWidgetData();
      } catch (error) {
        console.error('Error parsing change log data:', error);
        this.resetData();
      }
    },
    
    // Parse additive widget data
    parseAdditiveWidgetData() {
      ADDITIVE_WIDGETS.forEach(widget => {
        this[widget.originalProp] = this.originalModel[widget.modelKey] || [];
        const changedData = this.changedModel[widget.modelKey] || [];
        
        // If there's no change data, create empty structure matching original
        // This ensures we show blank fields instead of "No X" message
        if (changedData.length === 0 && this[widget.originalProp].length > 0) {
          // Create empty items matching the original structure
          this[widget.changedProp] = this[widget.originalProp].map(() => ({}));
        } else {
          // API now provides enriched data with _changedFields metadata
          this[widget.changedProp] = changedData;
        }
      });
    },
    
    // Reset all data
    resetData() {
      this.originalModel = {};
      this.changedModel = {};
      ADDITIVE_WIDGETS.forEach(widget => {
        this[widget.originalProp] = [];
        this[widget.changedProp] = [];
      });
    },
    
    // Add fallback display names
    addFallbackDisplayNames(model) {
      const result = { ...model };
      
      FALLBACK_MAPPINGS.forEach(mapping => {
        if (result[mapping.fk] && !result[mapping.name]) {
          result[mapping.name] = `ID: ${result[mapping.fk]}`;
        }
      });
      
      return result;
    },
    
    // Handle approve action
    handleApprove() {
      if (this.processing) {
        return;
      }
      this.processing = true;
      this.$emit('approve');
    },
    
    // Handle reject action
    handleReject() {
      if (this.processing) {
        return;
      }
      this.processing = true;
      this.$emit('reject');
    }
  }
};
</script>

<style lang="scss">
.readonly-form {
  opacity: 0.8;
}

// Consolidated highlighting styles
#page-change-logs-form {
  .changed-field-highlight,
  [data-changed="true"] {
    background-color: rgba(255, 234, 234, 1) !important;
    border-radius: 4px;
    transition: all 0.3s ease;

    input,
    select,
    textarea {
      background-color: rgba(255, 234, 234, 1) !important;
      transition: all 0.3s ease;

      &:focus {
        border-color: rgba(255, 234, 234, 0.4) !important;
        box-shadow: 0 0 0 2px rgba(255, 234, 234, 1) !important;
      }
    }

    label {
      font-weight: 600;
    }
  }

  // Specific targeting for vue-form-generator
  .form-group {
    &.changed-field-highlight,
    &[data-changed="true"] {
      background-color: rgba(255, 234, 234, 1) !important;
      border-radius: 4px;
      transition: all 0.3s ease;

      input,
      select,
      textarea {
        background-color: rgba(255, 234, 234, 1) !important;

        &:focus {
          border-color: rgba(255, 234, 234, 0.4) !important;
          box-shadow: 0 0 0 2px rgba(255, 234, 234, 1) !important;
        }
      }

      label {
        font-weight: 600;
      }
    }
  }

  // Alternative selectors for different form structures
  .field-wrap {
    &.changed-field-highlight,
    &[data-changed="true"] {
      background-color: rgba(255, 234, 234, 1) !important;
      border-radius: 4px;
    }
  }

  // Global targeting for any element with the changed class
  * {
    &.changed-field-highlight,
    &[data-changed="true"] {
      background-color: rgba(255, 234, 234, 1) !important;
      border-radius: 4px;
    }
  }

  // Relationship item highlighting
  .changed-relationship-item {
    background-color: rgba(255, 234, 234, 1) !important;
    border-left: 3px solid rgba(255, 0, 0, 0.3) !important;
    border-radius: 4px;
    transition: all 0.3s ease;

    .font-medium {
      color: rgba(255, 0, 0, 0.8) !important;
      font-weight: 600;
    }

    label {
      color: rgba(255, 0, 0, 0.7) !important;
      font-weight: 600;
    }
  }
}
</style>